<script setup lang="ts">
    import { useCaseSettingsViewModel } from '@/views/case-settings/composables/useCaseSettingsViewModel';
    import patientIcon from '@/assets/icons/user.svg';
    import { CASE_SETTINGS_TARGETS } from '@/domain/analytics/analyticsTargets';

    // Same store instance as the parent: the split adds no second source of form state.
    const vm = useCaseSettingsViewModel();
</script>

<template>
    <app-main-card class="pa-8">
        <div class="section-title-div">
            <img :src="patientIcon" alt="Patient Icon" width="20px" />
            <app-form-section-title class="ma-0"> Patient Information </app-form-section-title>
        </div>
        <app-form-subtitle
            class="ma-0 px-10 pt-2"
            :data-analytics-id="CASE_SETTINGS_TARGETS.patientInfoIntro"
        >
            Patient details are not able to be changed once scans have been uploaded. Please ensure
            all details are correct before uploading DICOM files for this case.
        </app-form-subtitle>

        <v-container class="px-10 pt-0">
            <app-form-group label="First Name" input-id="patient-first-name-input">
                <app-text-input
                    v-model="vm.patient.firstName"
                    input-id="patient-first-name-input"
                    data-test-id="first-name-input"
                    :disabled="vm.hasStudy || vm.isCaseReadonly"
                    :error-messages="
                        vm.showFieldError('patientFirstName')
                            ? 'Please provide patient first name'
                            : ''
                    "
                />
            </app-form-group>

            <app-form-group label="Middle Name(s)" optional input-id="patient-middle-names-input">
                <app-text-input
                    v-model="vm.patient.middleNames"
                    input-id="patient-middle-names-input"
                    data-test-id="middle-name-input"
                    :disabled="vm.hasStudy || vm.isCaseReadonly"
                />
            </app-form-group>

            <app-form-group
                label="Family Name"
                input-id="patient-family-name-input"
                :data-analytics-id="CASE_SETTINGS_TARGETS.familyName"
            >
                <app-text-input
                    v-model="vm.patient.familyName"
                    input-id="patient-family-name-input"
                    data-test-id="family-name-input"
                    :disabled="vm.hasStudy || vm.isCaseReadonly"
                    :error-messages="
                        vm.showFieldError('patientFamilyName')
                            ? 'Please provide patient family name'
                            : ''
                    "
                />
            </app-form-group>

            <app-form-group label="Sex" :data-analytics-id="CASE_SETTINGS_TARGETS.patientSex">
                <app-toggle
                    v-model="vm.patient.sex"
                    :disabled="vm.hasStudy || vm.isCaseReadonly"
                    :error-messages="
                        vm.showFieldError('patientSex') ? 'Please provide patient sex' : ''
                    "
                    :options="[
                        { label: 'Male', value: 'male' },
                        { label: 'Female', value: 'female' },
                    ]"
                    data-testid="sex-selector"
                />
            </app-form-group>

            <app-form-group
                label="Date of Birth"
                input-id="patient-dob-input"
                :data-analytics-id="CASE_SETTINGS_TARGETS.patientDob"
            >
                <app-text-input
                    v-model="vm.patient.dateOfBirth"
                    type="date"
                    input-id="patient-dob-input"
                    data-test-id="date-of-birth-input"
                    :disabled="vm.hasStudy || vm.isCaseReadonly"
                    :error-messages="
                        vm.showFieldError('patientDateOfBirth')
                            ? 'Please provide patient date of birth'
                            : ''
                    "
                />
            </app-form-group>
        </v-container>
    </app-main-card>
</template>

<style scoped lang="scss">
    /* Carried with the markup: scoped styles do not cross a component boundary. */
    :deep(.v-field.v-field--variant-outlined) {
        border-radius: 4px !important;
    }

    .section-title-div {
        display: flex;
        align-items: center;
        gap: 16px;
    }
</style>
